import { useEffect, useState, useCallback, useContext } from "react";
import { useParams } from "react-router-dom";
import { useDebounce } from "../hooks";
import { Context } from "../Context";
import SeasonDropdownMenu from "./SeasonDropdownMenu";

const Leaderboard = () => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const context = useContext(Context);
  const accessToken = context.accessToken;
  const serverId = useParams().serverId;
  const currentYear = new Date().getFullYear();
  const currentSeason = currentYear - 2024 + 1;
  const limit = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const debounceCurrentPage = useDebounce(currentPage, 300);
  const [totalPages, setTotalPages] = useState(1);
  const [season, setSeason] = useState(currentSeason);
  const [leaderboardData, setLeaderboardData] = useState([]);

  const getLeaderboardData = useCallback(
    async (season) => {
      if (!accessToken) return; // Wait until tokens are available

      const url = `${apiBaseUrl}/api/servers/${serverId}/leaderboard?season=${season}&page=${debounceCurrentPage}&limit=${limit}`;

      try {
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`, // Supabase JWT in header
          },
        });

        if (response.status === 400) {
          throw new Error("Bad Request: No season provided in query parameter");
        }

        const data = await response.json();
        return data;
      } catch (error) {
        console.error("Failed to fetch leaderboard data", error);
        return null;
      }
    },
    [apiBaseUrl, serverId, debounceCurrentPage, limit, accessToken]
  );

  useEffect(() => {
    // Get the leaderboard stats from the API
    const leaderboardData = async () => {
      const data = await getLeaderboardData(season);

      // Ensure we got the data
      if (!data) {
        setLeaderboardData([]);
        return;
      }
      const seasonData = data["leaderboardData"];
      if (!seasonData) {
        setLeaderboardData([]);
        return;
      }

      const leaderboardData = [];
      for (let i = 0; i < seasonData.length; i++) {
        const entry = seasonData[i];

        let rank = i + 1 + limit * (currentPage - 1);
        let rankHighlighting = "";
        if (i === 0 && rank === 1) {
          rank = "🥇";
          rankHighlighting = "text-[0.75rem] md:text-[1.125rem] font-[600]";
        } else if (i === 1 && rank === 2) {
          rank = "🥈";
          rankHighlighting = "text-[0.7rem] md:text-[1.075rem] font-[550]";
        } else if (i === 2 && rank === 3) {
          rank = "🥉";
          rankHighlighting = "text-[0.65rem] md:text-[1.025rem] font-[500]";
        }

        const row = (
          <tr
            key={i}
            className={i % 2 === 1 ? "bg-[var(--secondary-colour)]" : ""}
          >
            <td className="px-4 py-2 text-center text-[0.6rem] md:text-base">{rank}</td>
            <td
              className={`px-4 py-2 border-l border-[#74ddaa] text-[0.6rem] md:text-base ${rankHighlighting}`}
            >
              {entry.displayName}
            </td>
            <td
              className={`px-4 py-2 border-l border-[#74ddaa] text-[0.6rem] md:text-base ${rankHighlighting}`}
            >
              {entry.totalCatches}
            </td>
            <td
              className={`px-4 py-2 border-l border-[#74ddaa] text-[0.6rem] md:text-base ${rankHighlighting}`}
            >
              {entry.lastCatchDate} ({entry.daysSinceLastCatch} days ago)
            </td>
          </tr>
        );

        leaderboardData.push(row);
      }

      setLeaderboardData(leaderboardData);
      setTotalPages(data["totalPages"]);
    };

    leaderboardData();
  }, [getLeaderboardData, season, currentPage]);

  const updateSeason = (newSeason) => {
    setSeason(newSeason);
    setCurrentPage(1);
  };

  return (
    <div className="bg-[var(--tertiary-colour)] rounded-2xl p-2 mb-8">
      <div className="ml-2">
        <SeasonDropdownMenu onChange={updateSeason} />
      </div>

      <div className="overflow-hidden rounded-xl border-4 border-[var(--secondary-colour)]">
        <table className="min-w-full table-auto">
          <thead className="bg-[var(--secondary-colour)] border-b border-[#74ddaa]">
            <tr>
              <th className="px-2 py-2 text-left font-semibold w-0">Rank</th>
              <th className="px-4 py-2 text-left font-semibold border-l border-[#74ddaa]">
                User
              </th>
              <th className="px-4 py-2 text-left font-semibold border-l border-[#74ddaa]">
                Total Fish
              </th>
              <th className="px-4 py-2 text-left font-semibold border-l border-[#74ddaa]">
                Last Catch
              </th>
            </tr>
          </thead>
          <tbody>
            {leaderboardData.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-4 py-2 text-center">
                  No data available.
                </td>
              </tr>
            ) : (
              leaderboardData
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between mx-2 mt-1 items-center">
        {currentPage > 1 ? (
          <button
            className="hover:text-dimWhite"
            onClick={() => {
              if (currentPage > 1) setCurrentPage(currentPage - 1);
            }}
          >
            Prev
          </button>
        ) : (
          <div className="mr-8"></div>
        )}

        {totalPages > 1 && (
          <h3>
            {currentPage} / {totalPages}
          </h3>
        )}

        {currentPage < totalPages ? (
          <button
            className="hover:text-dimWhite"
            onClick={() => {
              if (currentPage < totalPages) setCurrentPage(currentPage + 1);
            }}
          >
            Next
          </button>
        ) : (
          <div className="ml-8"></div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
